import dynamic from "next/dynamic";
import useEmblaCarousel from "embla-carousel-react";
import {
  CalculatorIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from "@phosphor-icons/react";
import { useState, useCallback, useEffect } from "react";

// Dynamically import ShippingCalculator to avoid SSR issues
const ShippingCalculator = dynamic(
  () => import("@cartbot/shipping-calculator"),
  { ssr: false }
);

const products = [
  {
    id: 0,
    sku: "TXR799",
    brand: "TAG",
    title: "TAG™ Towbar to suit Nissan Patrol Y62 Wagons (12/2012 - on)",
    price: 1139,
    weight: 35,
    image: "./TXR799.jpg",
  },
  {
    id: 1,
    sku: "40047-85",
    brand: "Curt",
    title: "Curt RockerBall™ - ShockDrop 50mm Tow Ball",
    price: 98.5,
    weight: 1.2,
    image: "./40047-85.jpg",
  },
  {
    id: 2,
    sku: "TREDCPGTGR",
    brand: "TRED",
    title: "TRED Outdoors™ GT Compact Recovery Device (Green)",
    price: 159,
    weight: 3.9,
    image: "./TREDCPGTGR.jpg",
  },
];

export default function ShippingCalculatorDemo() {
  const [currentProduct, setCurrentProduct] = useState(products[0]);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    const selectedIndex = emblaApi.selectedScrollSnap();
    setCurrentProduct(products[selectedIndex]);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  return (
    <div className="partbot-shipping-calculator z-10 flex w-full shrink flex-col items-end">
      <div className="absolute right-0 -mr-4 -mt-10 size-20 md:-mr-12 md:-mt-6 md:block md:size-28">
        <span className="absolute h-full w-full animate-ping rounded-full bg-amber-400 opacity-10 md:inline-flex"></span>
        <div className="absolute z-10 flex aspect-square size-20 rotate-12 transform items-center justify-center rounded-full bg-amber-500 md:size-28">
          <span className="text-center text-lg font-extrabold leading-none text-primary-foreground md:text-2xl">
            Try me!
          </span>
        </div>
      </div>
      <div className="mx-auto inline-block w-full max-w-[92dvw] transform space-y-5 rounded-xl border bg-white px-4 py-4 text-left align-bottom shadow-lg transition-all md:mx-0 md:my-8 md:max-w-md md:p-12 md:shadow-xl lg:max-w-lg lg:rounded-4xl">
        <div className="inline-flex items-center space-x-4 rounded-lg bg-stone-50 px-3 py-2 text-stone-600">
          <CalculatorIcon size={42} />
          <span className="text-xs">
            Reduce abandoned carts and increase conversions with live shipping rates on your product pages.
          </span>
        </div>

        <div className="relative mb-5 h-max">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {products.map((product) => (
                <div key={product.sku} className="min-w-0 flex-[0_0_100%] px-1">
                  <div className="flex h-full w-full space-x-4 rounded-lg border border-dashed border-stone-200 bg-stone-50/50 p-4">
                    <div className="h-[70px] w-[70px] shrink-0 overflow-clip rounded-lg border border-stone-300 bg-white p-1">
                      <img
                        src={product.image}
                        className="object-contain object-center"
                        alt={product.title}
                      />
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm font-bold tracking-tight text-stone-600 md:pr-16">
                        {product.title}
                      </div>
                      <div className="pt-1 text-xs font-bold tracking-tight text-stone-500">
                        {product.price.toLocaleString("en-AU", {
                          style: "currency",
                          currency: "AUD",
                        })}
                        <span className="ml-2 rounded bg-stone-300 px-1.5 py-0.5 font-medium">
                          {product.weight}kg
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <button
            className="group absolute left-0 top-1/2 -ml-3 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-stone-200 transition hover:bg-stone-600 disabled:opacity-30"
            onClick={scrollPrev}
            disabled={!canScrollPrev}
          >
            <CaretLeftIcon className="h-2 w-2 text-stone-500 transition group-hover:text-white" />
          </button>
          <button
            className="group absolute right-0 top-1/2 -mr-3 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-stone-200 transition hover:bg-stone-600 disabled:opacity-30"
            onClick={scrollNext}
            disabled={!canScrollNext}
          >
            <CaretRightIcon className="h-2 w-2 text-stone-500 transition group-hover:text-white" />
          </button>
        </div>

        {process.env.NEXT_PUBLIC_PARTBOT_API_KEY && (
          <div className="partbot-shipping-calculator-scope">
            <ShippingCalculator
              sku={currentProduct.sku}
              brand={currentProduct.brand}
              weight={currentProduct.weight}
              price={currentProduct.price}
              apiKey={process.env.NEXT_PUBLIC_PARTBOT_API_KEY}
            />
          </div>
        )}
      </div>
    </div>
  );
}
